/**
 * Date manipulation utilities
 * Provides dayjs equivalents for date-fns manipulation functions
 */
import dayjs, { type Dayjs } from './dayjs-config';
import { toDayjs } from './parse-iso-safe';

/**
 * Add minutes to a date
 * Equivalent to date-fns's addMinutes
 *
 * @param date - Base date
 * @param amount - Number of minutes to add
 * @returns New date with minutes added
 */
export function addMinutes(date: Date | Dayjs | string | null | undefined, amount: number): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  return dayjsDate.add(amount, 'minute');
}

/**
 * Subtract minutes from a date
 * Equivalent to date-fns's subMinutes
 *
 * @param date - Base date
 * @param amount - Number of minutes to subtract
 * @returns New date with minutes subtracted
 */
export function subMinutes(date: Date | Dayjs | string | null | undefined, amount: number): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  return dayjsDate.subtract(amount, 'minute');
}

/**
 * Add days to a date
 * Equivalent to date-fns's addDays
 *
 * @param date - Base date
 * @param amount - Number of days to add
 * @returns New date with days added
 */
export function addDays(date: Date | Dayjs | string | null | undefined, amount: number): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  return dayjsDate.add(amount, 'day');
}

/**
 * Subtract days from a date
 * Equivalent to date-fns's subDays
 *
 * @param date - Base date
 * @param amount - Number of days to subtract
 * @returns New date with days subtracted
 */
export function subDays(date: Date | Dayjs | string | null | undefined, amount: number): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  return dayjsDate.subtract(amount, 'day');
}

/**
 * Add hours to a date
 *
 * @param date - Base date
 * @param amount - Number of hours to add
 * @returns New date with hours added
 */
export function addHours(date: Date | Dayjs | string | null | undefined, amount: number): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  return dayjsDate.add(amount, 'hour');
}

/**
 * Subtract hours from a date
 *
 * @param date - Base date
 * @param amount - Number of hours to subtract
 * @returns New date with hours subtracted
 */
export function subHours(date: Date | Dayjs | string | null | undefined, amount: number): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  return dayjsDate.subtract(amount, 'hour');
}

/**
 * Set the hours and minutes of a date
 *
 * @param date - Base date
 * @param hours - Hours to set (0-23)
 * @param minutes - Minutes to set (0-59)
 * @returns New date with hours and minutes set
 */
export function setTime(date: Date | Dayjs | string | null | undefined, hours: number, minutes = 0): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  return dayjsDate.hour(hours).minute(minutes).second(0).millisecond(0);
}

/**
 * Parse hours and minutes string (HH:mm) and create a date for today
 *
 * @param hoursMinutes - Time string in format "HH:mm"
 * @returns dayjs instance with today's date and the specified time
 */
export function parseHoursMinutes(hoursMinutes: string): Dayjs | null {
  if (!hoursMinutes || !hoursMinutes.includes(':')) return null;

  const [hoursStr, minutesStr] = hoursMinutes.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (isNaN(hours) || isNaN(minutes)) return null;

  return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
}

/**
 * Set time on a specific date from a time string (HH:mm)
 *
 * @param date - Base date to set time on
 * @param timeString - Time string in format "HH:mm"
 * @returns dayjs instance with the date from base date and time from timeString
 *
 * @example
 * setTimeOnDate('2024-01-15', '14:30') // 2024-01-15 14:30:00
 * setTimeOnDate(new Date('2024-01-15'), '09:00') // 2024-01-15 09:00:00
 */
export function setTimeOnDate(date: Date | Dayjs | string | null | undefined, timeString: string): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  if (!timeString || !timeString.includes(':')) return null;

  const [hoursStr, minutesStr] = timeString.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (isNaN(hours) || isNaN(minutes)) return null;

  return dayjsDate.hour(hours).minute(minutes).second(0).millisecond(0);
}
